import { StoreEntryChoices } from "../../components/dtp/StoreEntryChoices";
import { trackStoreEvent } from "../../lib/dtpAnalytics";
import { dtpPath } from "../../lib/clinicSite";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  EducationalHero,
  EducationalCategories,
} from "../../components/dtp/educational/EducationalHomeSections";
import { CustomStoreView } from "../../components/store/custom/CustomStoreView";
import { EducationalShell } from "../../components/dtp/educational/EducationalShell";
import { useEducationalCatalog } from "../../components/dtp/educational/useEducationalCatalog";
import { doseFamilyKey, useDtpCartStore } from "../../store/useDtpCartStore";
import { trackDtpEvent } from "../../lib/dtpAnalytics";

export function DtpLearn() {
  const navigate = useNavigate();
  const addItem = useDtpCartStore((s) => s.addItem);
  const { intakePrograms, loading, error, clinic, products, categoryContent, physicianId } =
    useEducationalCatalog();
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!loading && clinic && !trackedRef.current) {
      trackedRef.current = true;
      trackDtpEvent({
        physicianId: clinic.physicianId,
        variant: clinic.storefrontVariant,
        eventType: "landing_view",
      });
    }
  }, [loading, clinic]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--store-accent,#2d4a3a)]" />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className="min-h-screen bg-[var(--store-bg,#faf8f4)] flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <h1 className="font-serif text-2xl text-[var(--store-text,#1a1612)]">
            {error || "Store unavailable"}
          </h1>
          <p className="text-[var(--store-muted,#8a8175)] mt-2">
            If this is a mistake, contact the clinic directly.
          </p>
        </div>
      </div>
    );
  }

  const customDesign =
    clinic.storefrontVariant === "custom" ? clinic.customDesign : null;
  const openProduct = (id: string) => {
    const page = customDesign?.productPages?.find((p) => p.productId === id);
    if (page) {
      navigate(dtpPath(physicianId, `/products/${page.slug}`));
      return;
    }
    const product = products.find((p) => p._id === id);
    if (product?.primaryCategorySlug && product.drugFamilySlug) {
      navigate(
        dtpPath(
          physicianId,
          `/learn/${product.primaryCategorySlug}/${product.drugFamilySlug}`,
        ),
      );
    } else if (product) {
      addItem({
        productId: product._id,
        productName: product.displayName,
        price: product.price,
        family: doseFamilyKey(product),
      });
      trackStoreEvent("add_to_cart", { productId: product._id, source: "featured" });
      navigate(dtpPath(physicianId, "/checkout"));
    }
  };

  return (
    <EducationalShell clinic={clinic}>
      <StoreEntryChoices flow={clinic.entryFlow} programs={intakePrograms} physicianId={physicianId}>
      {customDesign ? (
        <div className="max-w-[1200px] mx-auto px-5 md:px-8">
          <CustomStoreView
            design={customDesign}
            clinicName={clinic.name}
            coverage={clinic.coverage}
            products={products}
            categoryContent={categoryContent}
            assetUrl={(id) =>
              "/api/dtp/custom-store/" + physicianId + "/assets/" + id
            }
            onCategory={(slug) =>
              navigate(dtpPath(physicianId, `/learn/${slug}`))
            }
            onProduct={openProduct}
          />
        </div>
      ) : (
        <div style={{ containerType: "inline-size" }}>
          <EducationalHero
            clinic={clinic}
            products={products}
            onProduct={openProduct}
          />
          <EducationalCategories
            products={products}
            categoryContent={categoryContent}
            onCategory={(slug) =>
              navigate(dtpPath(physicianId, `/learn/${slug}`))
            }
          />
        </div>
      )}
      </StoreEntryChoices>
    </EducationalShell>
  );
}

export default DtpLearn;
